import { z } from 'zod';

import { api } from '@/shared/api';
import { unwrap } from '@/shared/api/envelope';

import { templateCostSourceResourceSchema, type TemplateCostSourceResource } from './types';

/**
 * Waar de standaardbedragen van één vragenlijst vandaan komen. Alles is een
 * telling van antwoordopties met een bedrag; een eenheidsprijs van € 1 telt als
 * "zelf invullen" en niet mee in het laagste en hoogste bedrag.
 */
export interface TemplateCostSource {
    templateUuid: string;
    templateName: string;
    /** De vragenlijst van het platform, niet een eigen lijst van het bedrijf. */
    isPlatform: boolean;
    versionUuid: string;
    version: number;
    /** Het versienummer uit het oude systeem, als de lijst daaruit komt. */
    legacyVersion: number | null;
    sourceLabel: string;
    importedAt: string | null;
    publishedAt: string | null;
    optionsWithAmounts: number;
    byUrgency: { direct: number; termijn: number; onderhoud: number };
    /** Een prijs per eenheid, keer de hoeveelheid die de inspecteur invult. */
    unitPriceOptions: number;
    /** Eenheidsprijs € 1: de inspecteur tikt het bedrag zelf in. */
    enteredAmountOptions: number;
    fixedAmountOptions: number;
    lowestAmount: number | null;
    highestAmount: number | null;
    /** `name` is `null` voor opties zonder kostengroep. */
    costGroups: Array<{ name: string | null; options: number }>;
    nhg: {
        nodesWithCode: number;
        codesUsed: string[];
        codesUnused: string[];
        /** Opties met een bedrag onder een knoop zonder NHG-onderdeel: die kosten vallen buiten de bijlage. */
        optionsWithoutCode: number;
    };
    /** `null`: de vragenlijst legt het prijspeil niet vast. */
    priceLevel: string | null;
    /** `null`: de vragenlijst zegt niet of de bedragen inclusief btw zijn. */
    vatIncluded: boolean | null;
}

function amount(value: string | null): number | null {
    return value === null ? null : Number(value);
}

export function toTemplateCostSource(resource: TemplateCostSourceResource): TemplateCostSource {
    return {
        templateUuid: resource.template_uuid,
        templateName: resource.template_name,
        isPlatform: resource.is_platform,
        versionUuid: resource.version_uuid,
        version: resource.version,
        legacyVersion: resource.legacy_version,
        sourceLabel: resource.source_label,
        importedAt: resource.imported_at,
        publishedAt: resource.published_at,
        optionsWithAmounts: resource.options_with_amounts,
        byUrgency: { ...resource.by_urgency },
        unitPriceOptions: resource.unit_price_options,
        enteredAmountOptions: resource.entered_amount_options,
        fixedAmountOptions: resource.fixed_amount_options,
        lowestAmount: amount(resource.lowest_amount),
        highestAmount: amount(resource.highest_amount),
        costGroups: resource.cost_groups.map((group) => ({ ...group })),
        nhg: {
            nodesWithCode: resource.nhg.nodes_with_code,
            codesUsed: [...resource.nhg.codes_used],
            codesUnused: [...resource.nhg.codes_unused],
            optionsWithoutCode: resource.nhg.options_without_code,
        },
        priceLevel: resource.price_level,
        vatIncluded: resource.vat_included,
    };
}

/**
 * `GET /template-cost-sources`: alleen inzien, alleen voor wie de instellingen
 * beheert (`settings.manage`); de API geeft iedereen anders een 403.
 */
export const templateCostSourcesApi = {
    async list(): Promise<TemplateCostSource[]> {
        return unwrap(
            z.array(templateCostSourceResourceSchema),
            await api.get('/template-cost-sources'),
        ).map(toTemplateCostSource);
    },
};
